import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, LogOut, Save, AlertTriangle, Send, CarFront, BookOpen } from "lucide-react";
import { ResponseRowActions, NoResponseActions } from "../components/ResponseRowActions";
import { HorsEffectifAlert } from "../components/HorsEffectifAlert";
import { api, coachHeaders, formatInstall, TYPES, typeLabel, toTimeInput, defautsType, ADRESSE_CLUB, heureAff } from "../lib/api";
import { Brand } from "../components/Logo";
import { openWhatsApp } from "../components/ShareWhatsApp";
import { Roster } from "../components/Roster";
import { CatBadge } from "../components/CatBadge";
import { joueurKey, nomCat } from "../lib/categorie";
import { BirthdayBanner } from "../components/BirthdayBanner";
import { StaffSettings } from "../components/StaffSettings";
import { Archives } from "../components/Archives";
import { UpdatePanel } from "../components/UpdatePanel";
import { ConvocationBar } from "../components/ConvocationBar";
import { PlayersDirectory } from "../components/PlayersDirectory";
import { AccessLog } from "../components/AccessLog";
import { BackupsPanel } from "../components/BackupsPanel";
import { SecurityAlert } from "../components/SecurityAlert";
import { SeasonPresence } from "../components/SeasonPresence";
import { AideEditor } from "../components/AideEditor";
import { FichesRecues } from "../components/FichesRecues";
import { useIdleLogout, useNoIndex, isIdleExpired, IDLE_KEY } from "../lib/useIdleLogout";

const KEY = "coach_code";

const Gate = ({ onOk }) => {
  const [code, setCode] = useState("");
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    const clean = code.trim();
    try {
      await api.post("/coach/verify", {}, coachHeaders(clean));
      sessionStorage.setItem(KEY, clean);
      onOk(clean);
    } catch (e) {
      setErr(e?.response?.status === 429 ? e.response.data?.detail : "Code invalide");
    }
  };
  return (
    <form onSubmit={submit} data-testid="coach-pin-form" className="mx-auto mt-24 max-w-md rounded-[28px] border border-line bg-pitch p-8">
      <h1 className="font-display text-5xl uppercase text-chalk">Espace coach</h1>
      <p className="mt-2 text-dim">Entre ton code coach ou délégué pour voir les réponses.</p>
      <input data-testid="coach-pin-input" type="password" autoCapitalize="none" autoCorrect="off" autoComplete="off" spellCheck={false} value={code} onChange={(e) => { setCode(e.target.value); setErr(""); }} placeholder="Code" className="mt-6 w-full rounded-2xl border border-line bg-ink px-5 py-4 text-chalk outline-none focus:border-lime" />
      {err && <p data-testid="coach-pin-error" className="mt-2 text-sm text-signal">{err}</p>}
      <button data-testid="coach-pin-submit-button" className="mt-6 w-full rounded-full bg-chalk py-4 font-semibold text-ink transition-colors duration-300 hover:bg-lime">Entrer</button>
    </form>
  );
};

const Stat = ({ id, label, value, cls = "bg-pitch text-chalk" }) => (
  <div data-testid={`coach-stat-${id}`} className={`rounded-2xl border border-line px-4 py-3 ${cls}`}>
    <p className="font-mono text-[11px] uppercase tracking-[0.15em] opacity-80">{label}</p>
    <p className="mt-1 font-display text-3xl leading-none sm:text-4xl">{value}</p>
  </div>
);

const frDate = (iso) => {
  if (!iso) return "[Date du match]";
  const s = new Date(`${iso}T12:00:00`).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });
  return s.charAt(0).toUpperCase() + s.slice(1);
};

const TIME_FIELDS = ["heure", "rendez_vous", "heure_limite", "covoit_heure"];

const MatchEditor = ({ code, conv, onSaved }) => {
  const [m, setM] = useState({ ...conv, adresse: conv.adresse || conv.lieu || "", heure: toTimeInput(conv.heure), rendez_vous: toTimeInput(conv.rendez_vous) });
  const saveModele = async () => {
    if (!window.confirm(`Enregistrer ces infos comme modèle « ${typeLabel(m.type)} » pour les prochaines convocations ?`)) return;
    try { await api.put(`/modeles/${m.type || "match"}`, { ...m, lieu: "" }, coachHeaders(code)); toast.success(`Modèle « ${typeLabel(m.type)} » enregistré`); }
    catch { toast.error("Échec de l'enregistrement du modèle"); }
  };
  const type = m.type || "match";
  const ADRESSE_LBL = { match: "Adresse du match", entrainement: "Adresse de l'entraînement", tournoi: "Adresse du tournoi" };
  const fields = [["club", "Club"], ["adversaire", type === "tournoi" ? "Nom du tournoi" : "Adversaire"], ["competition", "Compétition"], ["date", "Date"], ["heure", type === "match" ? "Coup d'envoi" : "Début"], ["rendez_vous", "Rendez-vous"], ["date_limite", "Réponse obligatoire avant le"], ["heure_limite", "Heure limite de réponse"], ["adresse", ADRESSE_LBL[type]],
    ...(type === "entrainement" ? [] : [["covoit_lieu", "Lieu de covoiturage (sans voiture)"], ["covoit_heure", "Heure de covoiturage"]])];
  const DATE_FIELDS = { date: "date_iso", date_limite: "date_limite_iso" };
  const save = async (e) => {
    e.preventDefault();
    try { await api.put(`/convocations/${conv.id}`, { ...m, lieu: "" }, coachHeaders(code)); toast.success("Infos enregistrées"); onSaved(); }
    catch { toast.error("Échec de l'enregistrement"); }
  };
  return (
    <form onSubmit={save} data-testid="coach-match-editor" className="rounded-2xl border border-line bg-pitch p-4">
      <h2 className="font-display text-2xl uppercase text-chalk">{{ match: "Infos du match", entrainement: "Infos de l'entraînement", tournoi: "Infos du tournoi" }[type]}</h2>
      <div className="mt-3">
        <span className="mb-1 block font-mono text-xs uppercase tracking-[0.2em] text-dim">Type</span>
        <div role="radiogroup" className="grid grid-cols-3 gap-2 sm:max-w-md">
          {TYPES.map(([k, l]) => (
            <button key={k} type="button" role="radio" aria-checked={(m.type || "match") === k} data-testid={`coach-match-type-${k}`} onClick={() => setM({ ...m, ...(k !== "entrainement" && type === "entrainement" ? { adversaire: m.adversaire === "Entraînement" ? "[Adversaire]" : m.adversaire, adresse: m.adresse === ADRESSE_CLUB ? "" : m.adresse } : {}), ...defautsType(k) })}
              className={`rounded-full border px-3 py-2 text-sm font-semibold transition-colors ${(m.type || "match") === k ? "border-lime bg-lime text-ink" : "border-line bg-ink text-chalk hover:border-lime"}`}>{l}</button>
          ))}
        </div>
      </div>
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        {fields.map(([k, l]) => (
          <label key={k} className={k === "adresse" ? "sm:col-span-2" : ""}>
            <span className="mb-1 block font-mono text-xs uppercase tracking-[0.2em] text-dim">{l}</span>
            {TIME_FIELDS.includes(k) ? (
              <input data-testid={`coach-match-${k}-input`} type="time" value={m[k] ?? ""} onChange={(e) => setM({ ...m, [k]: e.target.value })} className="w-full rounded-xl border border-line bg-ink px-3 py-2 text-chalk outline-none [color-scheme:dark] focus:border-lime" />
            ) : DATE_FIELDS[k] ? (
              <input data-testid={`coach-match-${k}-input`} type="date" value={m[DATE_FIELDS[k]] ?? ""} onChange={(e) => setM({ ...m, [DATE_FIELDS[k]]: e.target.value, [k]: e.target.value ? frDate(e.target.value) : (k === "date" ? "[Date du match]" : "") })} className="w-full rounded-xl border border-line bg-ink px-3 py-2 text-chalk outline-none [color-scheme:dark] focus:border-lime" />
            ) : (
              <input data-testid={`coach-match-${k}-input`} value={m[k] ?? ""} onChange={(e) => setM({ ...m, [k]: e.target.value })} className="w-full rounded-xl border border-line bg-ink px-3 py-2 text-chalk outline-none focus:border-lime" />
            )}
          </label>
        ))}
        <label className="sm:col-span-2">
          <span className="mb-1 block font-mono text-xs uppercase tracking-[0.2em] text-dim">Infos pratiques</span>
          <textarea data-testid="coach-match-infos-textarea" rows={3} value={m.infos_pratiques ?? ""} onChange={(e) => setM({ ...m, infos_pratiques: e.target.value })} className="w-full resize-none rounded-xl border border-line bg-ink px-3 py-2 text-chalk outline-none focus:border-lime" />
        </label>
        <label className="flex items-center gap-3 text-chalk sm:col-span-2">
          <input data-testid="coach-match-afficher-infos-checkbox" type="checkbox" checked={m.afficher_infos !== false} onChange={(e) => setM({ ...m, afficher_infos: e.target.checked })} className="h-5 w-5 shrink-0 accent-[#D4AF37]" />
          Afficher le bouton « Infos pratiques » aux joueurs
        </label>
        {type !== "entrainement" && (
        <label className="flex items-center gap-3 text-chalk sm:col-span-2">
          <input data-testid="coach-match-afficher-covoit-checkbox" type="checkbox" checked={!!m.afficher_covoit} onChange={(e) => setM({ ...m, afficher_covoit: e.target.checked })} className="h-5 w-5 shrink-0 accent-[#D4AF37]" />
          Afficher le rendez-vous covoiturage (heure et lieu) sous le rendez-vous
        </label>
        )}
        {(!m.covoit_lieu || !m.covoit_heure) && type !== "entrainement" && (
          <p data-testid="coach-covoit-missing-warning" role="alert" className="flex items-start gap-2 rounded-xl border border-signal bg-signal/15 p-3 text-sm text-chalk sm:col-span-2">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-signal" />
            {!m.covoit_lieu && !m.covoit_heure ? "Lieu et heure" : !m.covoit_lieu ? "Lieu" : "Heure"} de covoiturage non rempli{!m.covoit_lieu && !m.covoit_heure ? "s" : ""} : les familles qui choisissent « Rendez-vous au club » verront « seront précisés par le coach ». Remplis-les puis enregistre (astuce : enregistre-les aussi dans le modèle).
          </p>
        )}
        <label className="flex items-center gap-3 text-chalk">
          <input data-testid="coach-match-domicile-checkbox" type="checkbox" checked={!!m.domicile} onChange={(e) => setM({ ...m, domicile: e.target.checked })} className="h-5 w-5 accent-[#D4AF37]" />
          Match à domicile
        </label>
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button data-testid="coach-edit-match-button" className="inline-flex items-center gap-2 rounded-full bg-lime px-5 py-3 font-semibold text-ink"><Save className="h-4 w-4" />Enregistrer</button>
        <button type="button" data-testid="coach-save-modele-button" onClick={saveModele} className="inline-flex items-center gap-2 rounded-full border border-lime px-5 py-3 text-sm font-semibold text-lime transition-colors hover:bg-lime hover:text-ink">
          <Save className="h-4 w-4" />Enregistrer comme modèle « {typeLabel(m.type)} »
        </button>
      </div>
      <p className="mt-2 text-xs text-dim">Le modèle garde : compétition, heures, adresse, domicile, infos pratiques et covoiturage. Il pré-remplit les prochaines convocations de ce type.</p>
    </form>
  );
};

const transportTxt = (r) => (r.transport === "direct" ? "Direct" : r.transport === "sans_voiture" ? "Club · sans voiture" : r.transport === "places" ? (r.places ? `Club · ${r.places} place${r.places > 1 ? "s" : ""}` : "Club · voiture pleine") : "");

const CovoitSummary = ({ sansVoiture, conducteurs, places, match }) => (
  <div data-testid="coach-covoit-summary" className="mt-3 flex items-start gap-3 rounded-2xl border border-lime/40 bg-ink p-3 text-sm text-chalk">
    <CarFront className="mt-0.5 h-5 w-5 shrink-0 text-lime" />
    <div className="min-w-0">
      <p data-testid="coach-covoit-rdv" className="mb-1 font-mono text-xs uppercase tracking-wide text-lime">
        Rendez-vous covoiturage : {match?.covoit_heure || match?.covoit_lieu ? [match.covoit_heure && heureAff(match.covoit_heure), match.covoit_lieu].filter(Boolean).join(" — ") : <span className="text-signal">heure et lieu non remplis (onglet Infos)</span>}
      </p>
      <p className="font-semibold">
        Covoiturage : <span data-testid="coach-covoit-sans-voiture">{sansVoiture.length} enfant{sansVoiture.length > 1 ? "s" : ""} sans voiture</span> ·{" "}
        <span data-testid="coach-covoit-places" className={places < sansVoiture.length ? "text-signal" : "text-lime"}>{places} place{places > 1 ? "s" : ""} disponible{places > 1 ? "s" : ""}</span>
      </p>
      {sansVoiture.length > 0 && <p className="mt-1 break-words text-dim">Sans voiture : {sansVoiture.map((r) => `${r.prenom} ${r.nom.toUpperCase()}`).join(", ")}</p>}
      {conducteurs.length > 0 && <p className="mt-1 break-words text-dim">Places : {conducteurs.map((r) => `${r.prenom} ${r.nom.toUpperCase()} (${r.places ? r.places : "pleine"})`).join(", ")}</p>}
      <p data-testid="coach-transport-legend" className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-chalk">
        <span className="inline-flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full bg-emerald-400" />Direct</span>
        <span className="inline-flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full bg-sky-400" />Club avec places</span>
        <span className="inline-flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full bg-orange-400" />Club sans voiture</span>
      </p>
    </div>
  </div>
);

const Dashboard = ({ code, onLogout, onCodeChanged }) => {
  const [rows, setRows] = useState([]);
  const [joueurs, setJoueurs] = useState([]);
  const [filter, setFilter] = useState("all");
  const [convs, setConvs] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [user, setUser] = useState(null);
  const [tab, setTab] = useState("infos");
  const [canStaff, setCanStaff] = useState(false);
  const [version, setVersion] = useState(null);
  const [fichesNew, setFichesNew] = useState(0);
  const [dirKey, setDirKey] = useState(0);
  useEffect(() => {
    const lire = () => api.get("/fiches/compteur", coachHeaders(code)).then((r) => setFichesNew(r.data?.nouvelles ?? 0)).catch(() => {});
    lire();
    const t = setInterval(lire, 60000);
    return () => clearInterval(t);
  }, [code]);
  useEffect(() => { api.get("/version", coachHeaders(code)).then((r) => setVersion(r.data ?? null)).catch(() => {}); }, [code]);
  useEffect(() => { api.get("/staff", coachHeaders(code)).then(() => setCanStaff(true)).catch(() => setCanStaff(false)); }, [code]);
  useEffect(() => { api.get("/coach/me", coachHeaders(code)).then((r) => setUser(r.data ?? null)).catch(() => {}); }, [code]);
  const loadConvs = useCallback((prefer) => {
    api.get("/convocations", coachHeaders(code)).then((r) => {
      const list = Array.isArray(r.data) ? r.data : [];
      setConvs(list);
      setSelectedId((cur) => {
        const want = prefer === undefined ? cur : prefer;
        if (want && list.some((c) => c.id === want)) return want;
        return (list.find((c) => c.active) ?? list[0])?.id ?? null;
      });
    }).catch(() => onLogout());
  }, [code, onLogout]);
  useEffect(() => { loadConvs(); }, [loadConvs]);
  const match = convs.find((c) => c.id === selectedId) ?? null;
  const load = useCallback(() => {
    if (!selectedId) { setRows([]); setJoueurs([]); return; }
    const opts = { ...coachHeaders(code), params: { convocation_id: selectedId } };
    api.get("/reponses", opts).then((r) => setRows(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    api.get("/joueurs", opts).then((r) => setJoueurs(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, [code, selectedId]);
  useEffect(() => { load(); }, [load]);
  const del = async (id) => { await api.delete(`/reponses/${id}`, coachHeaders(code)); load(); };
  const presents = rows.filter((r) => r.statut === "present").length;
  const sansVoiture = rows.filter((r) => r.statut === "present" && r.transport === "sans_voiture");
  const placesDispo = rows.reduce((s, r) => s + (r.statut === "present" && r.transport === "places" ? r.places ?? 0 : 0), 0);
  const conducteurs = rows.filter((r) => r.statut === "present" && r.transport === "places");
  const alertes = rows.filter((r) => r.non_convoque && !r.hors_effectif);
  const repondus = new Set(rows.map((r) => `${r.nom.trim().toLowerCase()}|${r.prenom.trim().toLowerCase()}`));
  const sansReponse = joueurs.filter((j) => j.convoque && !repondus.has(`${j.nom.trim().toLowerCase()}|${j.prenom.trim().toLowerCase()}`));
  const shown = filter === "all" ? rows : rows.filter((r) => r.statut === filter);
  const joueurByKey = Object.fromEntries(joueurs.map((j) => [joueurKey(j.nom, j.prenom), j]));
  const tabs = [["all", "Tous"], ["present", "Présents"], ["absent", "Absents"], ["none", `Pas répondu (${sansReponse.length})`]];
  const voit = (k) => user?.admin || !Array.isArray(user?.onglets) || user.onglets.includes(k);
  const adminTabs = [
    ...(match ? [["infos", match.type === "entrainement" ? "Infos de l'entraînement" : match.type === "tournoi" ? "Infos du tournoi" : "Infos du match"], ...(match.type === "entrainement" ? [] : [["convoques", "Convocation"]]), ["reponses", "Réponses"]] : []),
    ["effectif", "Effectif"],
    ["presences", "Présences"],
    ["archives", "Convocations passées"],
  ].filter(([k]) => voit(k)).concat([
    ...(canStaff ? [["reglages", "Réglages"]] : []),
    ...(user?.admin ? [["maj", "Mise à jour"], ["securite", "Sécurité"], ["aide", "Page d'aide"]] : []),
  ]);
  const current = adminTabs.some(([k]) => k === tab) ? tab : adminTabs[0]?.[0];
  const groupes = [
    ["match", "Match", ["infos", "convoques", "reponses"]],
    ["equipe", "Équipe", ["effectif", "presences", "archives"]],
    ["club", "Réglages admin", ["reglages", "aide", "securite", "maj"]],
  ].map(([g, l, ks]) => [g, l, ks.map((k) => adminTabs.find(([x]) => x === k)).filter(Boolean)]).filter(([, , t]) => t.length);
  const groupeActif = groupes.find(([, , t]) => t.some(([k]) => k === current)) ?? groupes[0];
  const badgeFiches = fichesNew > 0 && <span data-testid="fiches-new-badge" title="Nouvelles fiches de renseignements" className="ml-1.5 rounded-full bg-signal px-1.5 py-px font-mono text-[11px] text-ink">{fichesNew}</span>;
  const nom = (x) => nomCat(x, joueurByKey[joueurKey(x.nom, x.prenom)] ?? x);
  const envoyerListe = () => {
    const p = rows.filter((r) => r.statut === "present");
    const a = rows.filter((r) => r.statut === "absent");
    openWhatsApp([
      `Feuille de match ${match?.club ?? ""} - ${match?.adversaire ?? ""} (${match?.date ?? ""})`,
      "",
      `PRÉSENTS (${p.length}) :`, ...p.map((r) => `- ${nom(r)}${transportTxt(r) ? ` (${transportTxt(r)})` : ""}`),
      "",
      ...(match?.type === "entrainement" ? [] : [
        `COVOITURAGE : ${sansVoiture.length} sans voiture, ${placesDispo} place${placesDispo > 1 ? "s" : ""} disponible${placesDispo > 1 ? "s" : ""}`,
        ...(match?.covoit_heure || match?.covoit_lieu ? [`RDV covoiturage : ${[match.covoit_heure && heureAff(match.covoit_heure), match.covoit_lieu].filter(Boolean).join(" — ")}`] : []),
        "",
      ]),
      `ABSENTS (${a.length}) :`, ...a.map((r) => `- ${nom(r)}${r.motif ? ` (${r.motif})` : ""}`),
      "",
      `PAS RÉPONDU (${sansReponse.length}) :`, ...sansReponse.map((j) => `- ${nom(j)}`),
    ].join("\n"));
  };
  const envoyerAide = () => openWhatsApp([
    "Bonjour à tous,",
    "Voici un petit guide pour vous aider à répondre aux convocations sur le site (présent / absent, covoiturage).",
    "",
    "📘 *GUIDE PARENTS*",
    `👉 ${window.location.origin}/aide`,
  ].join("\n"));

  return (
    <div data-testid="coach-dashboard-container" className="mx-auto max-w-7xl px-0 pb-12 pt-24 sm:px-10">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-4xl uppercase leading-none text-chalk sm:text-5xl">Feuille de match</h1>
          {user && <p data-testid="coach-current-user" className="mt-2 font-mono text-xs uppercase tracking-[0.15em] text-lime">Connecté : {user.nom} · {user.role === "coach" ? "Coach" : "Délégué"}</p>}
          {version?.version && <p data-testid="site-version" className="mt-1 font-mono text-[11px] text-dim">Version {version.version} - {version.hash}{version.installed_at ? ` · ${formatInstall(version.installed_at)}` : ""}</p>}
        </div>
        <div className="flex flex-col items-end gap-2 sm:flex-row sm:items-center">
          <button data-testid="coach-send-aide-button" onClick={envoyerAide} className="inline-flex items-center gap-2 rounded-full bg-[#25D366] px-4 py-2 text-sm font-semibold text-ink transition-transform duration-200 hover:-translate-y-0.5"><BookOpen className="h-4 w-4" />Envoyer l'aide<span className="hidden sm:inline"> sur WhatsApp</span></button>
          <button data-testid="coach-logout-button" onClick={onLogout} className="inline-flex items-center gap-2 rounded-full border border-line px-4 py-2 text-sm text-chalk hover:border-signal"><LogOut className="h-4 w-4" />Quitter</button>
        </div>
      </div>
      {user?.admin && <SecurityAlert code={code} onVoir={() => setTab("securite")} />}
      <BirthdayBanner code={code} />
      <ConvocationBar code={code} convs={convs} selectedId={selectedId} onSelect={setSelectedId} onChange={(id) => loadConvs(id)} admin={!!user?.admin} reponses={rows} />
      {match && (<>
      <div className="mt-6 grid grid-cols-3 gap-3">
        <Stat id="total" label="Réponses" value={rows.length} />
        <Stat id="presents" label="Présents" value={presents} cls="bg-lime text-ink" />
        <Stat id="absents" label="Absents" value={rows.length - presents} cls="bg-signal text-ink" />
      </div>
      {alertes.length > 0 && (
        <div data-testid="coach-non-convoque-alert" role="alert" className="mt-4 flex items-start gap-3 rounded-2xl border border-signal bg-signal/15 p-4 text-chalk">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-signal" />
          <div>
            <p className="font-semibold">Attention : {alertes.length} joueur{alertes.length > 1 ? "s" : ""} répond{alertes.length > 1 ? "ent" : ""} présent sans être convoqué{alertes.length > 1 ? "s" : ""}</p>
            <p className="mt-1 text-sm text-chalk/90">{alertes.map((r) => `${r.prenom} ${r.nom.toUpperCase()}`).join(", ")}</p>
          </div>
        </div>
      )}
      <HorsEffectifAlert rows={rows} code={code} convocationId={selectedId} onAdded={load} />
      </>)}
      <label className="mt-6 block sm:hidden">
        <span className="mb-1 block font-mono text-[11px] uppercase tracking-[0.15em] text-dim">Page</span>
        <select data-testid="coach-section-select" value={current} onChange={(e) => setTab(e.target.value)}
          className="w-full rounded-xl border border-lime/60 bg-pitch px-3 py-3 text-base font-semibold text-chalk outline-none focus:border-lime">
          {groupes.map(([g, l, t]) => (
            <optgroup key={g} label={l}>
              {t.map(([k, lt]) => <option key={k} value={k}>{lt}{k === "effectif" && fichesNew > 0 ? ` (${fichesNew} nouvelle${fichesNew > 1 ? "s" : ""} fiche${fichesNew > 1 ? "s" : ""})` : ""}</option>)}
            </optgroup>
          ))}
        </select>
      </label>
      <div className="mt-6 hidden sm:block">
        <div role="tablist" data-testid="coach-groups" className="flex gap-2">
          {groupes.map(([g, l, t]) => (
            <button key={g} role="tab" aria-selected={groupeActif?.[0] === g} data-testid={`coach-group-${g}`} onClick={() => setTab(t[0][0])}
              className={`rounded-full border px-5 py-2 font-display text-lg uppercase tracking-wide transition-colors ${groupeActif?.[0] === g ? "border-lime bg-lime text-ink" : "border-line text-chalk hover:border-lime hover:text-lime"}`}>
              {l}{g === "equipe" && fichesNew > 0 && <span data-testid="fiches-new-badge-group" className="ml-1.5 rounded-full bg-signal px-1.5 py-px font-mono text-[11px] text-ink">{fichesNew}</span>}
            </button>
          ))}
        </div>
        <div role="tablist" data-testid="admin-tabs" className="mt-3 flex gap-1 overflow-x-auto border-b border-line">
          {(groupeActif?.[2] ?? []).map(([k, l]) => (
            <button key={k} role="tab" aria-selected={current === k} data-testid={`admin-tab-${k}`} onClick={() => setTab(k)}
              className={`-mb-px shrink-0 rounded-t-xl border px-4 py-2 text-sm font-semibold transition-colors ${current === k ? "border-line border-b-ink bg-pitch text-lime" : "border-transparent text-dim hover:text-chalk"}`}>{l}
              {k === "effectif" && badgeFiches}</button>
          ))}
        </div>
      </div>
      <div className="mt-4 min-w-0">
        {current === "effectif" && <><FichesRecues code={code} onCount={setFichesNew} onIntegree={() => setDirKey((k) => k + 1)} /><PlayersDirectory key={dirKey} code={code} convocationId={selectedId} /></>}
        {current === "convoques" && match && <Roster code={code} match={match} reponses={rows} onChange={load} />}
        {current === "reponses" && match && (
        <div className="rounded-2xl border border-line bg-pitch p-4">
          <div className="flex flex-wrap gap-2">
            {tabs.map(([k, l]) => (
              <button key={k} data-testid={`coach-filter-${k}`} onClick={() => setFilter(k)} className={`rounded-full px-4 py-2 text-sm transition-colors duration-300 ${filter === k ? "bg-chalk text-ink" : "border border-line text-chalk hover:border-chalk/50"}`}>{l}</button>
            ))}
          </div>
          {(filter === "all" || filter === "present") && match.type !== "entrainement" && <CovoitSummary sansVoiture={sansVoiture} conducteurs={conducteurs} places={placesDispo} match={match} />}
          <button type="button" data-testid="coach-send-list-whatsapp-button" onClick={envoyerListe} className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#25D366] px-5 py-3 font-semibold text-ink transition-transform duration-300 hover:-translate-y-0.5">
            <Send className="h-4 w-4" /> M'envoyer la liste sur WhatsApp
          </button>
          <ul className="mt-3 divide-y divide-line">
            {filter === "none" && sansReponse.length === 0 && <li data-testid="coach-none-empty" className="py-6 text-dim">Tous les joueurs convoqués ont répondu.</li>}
            {filter === "none" && sansReponse.map((j) => (
              <li key={j.id} data-testid="coach-no-response-row" className="flex flex-wrap items-center justify-between gap-2 py-2.5">
                <p className="font-semibold text-chalk">{j.prenom} {j.nom.toUpperCase()}<CatBadge j={j} testid={`no-response-categorie-${j.id}`} /></p>
                {match?.type === "entrainement"
                  ? <NoResponseActions j={j} code={code} convocationId={selectedId} onChange={load} />
                  : <span className="rounded-full border border-line px-3 py-1 font-mono text-xs uppercase text-dim">Pas de réponse</span>}
              </li>
            ))}
            {filter !== "none" && shown.length === 0 && <li data-testid="coach-empty-list" className="py-6 text-dim">Aucune réponse pour l'instant.</li>}
            {filter !== "none" && shown.map((r) => (
              <li key={r.id} data-testid="player-response-row" className="flex flex-col gap-2 py-2.5 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
                <div className="min-w-0">
                  <p className="font-semibold text-chalk">{r.prenom} {r.nom.toUpperCase()}{joueurByKey[joueurKey(r.nom, r.prenom)] && <CatBadge j={joueurByKey[joueurKey(r.nom, r.prenom)]} testid={`response-categorie-${r.id}`} />}</p>
                  {r.motif && <p className="mt-1 break-words text-sm text-dim">« {r.motif} »</p>}
                  {r.raison_changement && <p data-testid={`coach-change-reason-${r.id}`} className="mt-1 break-words text-sm text-lime">Changement : « {r.raison_changement} »</p>}
                  <p className="mt-1 font-mono text-xs text-dim">{new Date(r.updated_at).toLocaleString("fr-FR")}</p>
                </div>
                <div className="flex flex-wrap items-center gap-2 sm:shrink-0 sm:justify-end">
                  {r.hors_effectif ? <span data-testid={`coach-hors-effectif-badge-${r.id}`} className="rounded-full border border-signal px-2 py-0.5 font-mono text-[11px] uppercase text-signal">Hors effectif</span>
                    : r.non_convoque && <span data-testid={`coach-non-convoque-badge-${r.id}`} className="rounded-full border border-signal px-2 py-0.5 font-mono text-[11px] uppercase text-signal">Non convoqué</span>}
                  <ResponseRowActions r={r} code={code} convocationId={selectedId} onChange={load} />
                  <button data-testid={`coach-delete-${r.id}`} onClick={() => del(r.id)} aria-label="Supprimer" className="text-dim transition-colors hover:text-signal"><Trash2 className="h-4 w-4" /></button>
                </div>
              </li>
            ))}
          </ul>
        </div>
        )}
        {current === "infos" && match && <MatchEditor key={selectedId} code={code} conv={match} onSaved={() => loadConvs()} />}
        {current === "archives" && <Archives code={code} convocationId={selectedId} onArchived={() => loadConvs(null)} />}
        {current === "reglages" && <StaffSettings code={code} user={user} onCodeChanged={onCodeChanged} />}
        {current === "maj" && <UpdatePanel code={code} />}
        {current === "presences" && <SeasonPresence code={code} />}
        {current === "securite" && <div className="grid gap-4 lg:grid-cols-2"><AccessLog code={code} /><BackupsPanel code={code} /></div>}
        {current === "aide" && <AideEditor code={code} />}
      </div>
    </div>
  );
};

export default function Coach() {
  const [code, setCode] = useState(() => {
    const c = sessionStorage.getItem(KEY);
    if (c && isIdleExpired()) { sessionStorage.removeItem(KEY); return null; }
    return c;
  });
  const logout = useCallback(() => { sessionStorage.removeItem(KEY); sessionStorage.removeItem(IDLE_KEY); setCode(null); }, []);
  const expire = useCallback(() => { logout(); toast.info("Déconnecté après 30 minutes sans activité"); }, [logout]);
  useIdleLogout(!!code, expire);
  useNoIndex();
  return (
    <div className="min-h-screen bg-ink px-5">
      <header className="fixed inset-x-0 top-0 z-50 bg-ink/70 backdrop-blur-xl">
        <div className="mx-auto max-w-7xl px-5 py-5 sm:px-10"><Brand /></div>
      </header>
      {code ? <Dashboard code={code} onLogout={logout} onCodeChanged={(c) => { sessionStorage.setItem(KEY, c); setCode(c); }} /> : <Gate onOk={setCode} />}
    </div>
  );
}
